// src/Components/ImportMarkers/CheckBatchesView.js

import React, { useState, useMemo, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';

const cellStyle = { paddingLeft: '8px', paddingRight: '8px' };

// --- CUSTOM TRIANGLE ICON ---
const CustomSortIcon = ({ active, className }) => {
  return (
    <svg className={className} focusable='false' viewBox='0 0 24 24' aria-hidden='true'>
      {active ? (
        <path d='M12 8l6 8H6z' fill='currentColor' />
      ) : (
        <path
          d='M12 8l6 8H6z'
          fill='none'
          stroke='currentColor'
          strokeWidth='1'
          strokeLinejoin='round'
        />
      )}
    </svg>
  );
};

const sortLabelSx = {
  '&.MuiTableSortLabel-root': { color: 'inherit' },
  '&.MuiTableSortLabel-root:hover': {
    color: 'inherit',
    '& .MuiTableSortLabel-icon': { opacity: 0.6 },
  },
  '& .MuiTableSortLabel-icon': {
    opacity: 0.4,
    width: '1.5em',
    height: '1.5em',
    color: '#000000',
    transition: 'opacity 0.2s, color 0.2s',
  },
  '&.Mui-active': { color: 'inherit' },
  '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1, color: '#000000' },
};

export default function CheckBatchesView({
  markers,
  categories = [],
  regions = [],
  isLoading,
  selectedBatchId,
  selectedIds,
  setSelectedIds,
}) {
  const [orderBy, setOrderBy] = useState('title');
  const [order, setOrder] = useState('asc');
  const lastClickedIndexRef = useRef(null);

  useEffect(() => {
    lastClickedIndexRef.current = null;
  }, [selectedBatchId, orderBy, order]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const enrichedAndSortedMarkers = useMemo(() => {
    const enriched = markers.map((row) => {
      const displayTitle =
        row.title || row.companyName || row.address || 'Unnamed Location';

      const categoryName = row.categoryId
        ? categories.find((c) => String(c.id) === String(row.categoryId))?.name ||
          row.category ||
          '-'
        : row.category || '-';

      const regionName = row.regionId
        ? regions.find((r) => String(r.id) === String(row.regionId))?.name ||
          row.region ||
          '-'
        : row.region || '-';

      return {
        ...row,
        displayTitle,
        categoryName,
        regionName,
      };
    });

    enriched.sort((a, b) => {
      let valA = '';
      let valB = '';

      if (orderBy === 'category') {
        valA = a.categoryName === '-' ? '' : a.categoryName.toLowerCase();
        valB = b.categoryName === '-' ? '' : b.categoryName.toLowerCase();
      } else if (orderBy === 'region') {
        valA = a.regionName === '-' ? '' : a.regionName.toLowerCase();
        valB = b.regionName === '-' ? '' : b.regionName.toLowerCase();
      } else {
        valA = a.displayTitle.toLowerCase();
        valB = b.displayTitle.toLowerCase();
      }

      // Always push empty ('-') values to the bottom regardless of asc/desc
      if (valA === '' && valB !== '') return 1;
      if (valA !== '' && valB === '') return -1;

      const cmp = valA.localeCompare(valB);
      if (cmp !== 0) {
        return order === 'asc' ? cmp : -cmp;
      }

      // Secondary sort by Title if Category or Region values are identical
      return a.displayTitle.toLowerCase().localeCompare(b.displayTitle.toLowerCase());
    });

    return enriched;
  }, [markers, categories, regions, orderBy, order]);

  const handleCheckboxClick = (e, id, index) => {
    e.stopPropagation();

    const isCurrentlySelected = selectedIds.includes(id);
    const shouldSelect = !isCurrentlySelected;

    if (e.shiftKey && lastClickedIndexRef.current !== null) {
      const start = Math.min(lastClickedIndexRef.current, index);
      const end = Math.max(lastClickedIndexRef.current, index);
      const rangeIds = enrichedAndSortedMarkers.slice(start, end + 1).map((r) => r.id);

      setSelectedIds((prev) => {
        const nextSet = new Set(prev);
        if (shouldSelect) {
          rangeIds.forEach((rId) => nextSet.add(rId));
        } else {
          rangeIds.forEach((rId) => nextSet.delete(rId));
        }
        return Array.from(nextSet);
      });
    } else {
      setSelectedIds((prev) => {
        if (prev.includes(id)) {
          return prev.filter((item) => item !== id);
        } else {
          return [...prev, id];
        }
      });
    }

    lastClickedIndexRef.current = index;
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(enrichedAndSortedMarkers.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const rowCount = markers.length;
  const numSelected = selectedIds.length;
  const isAllSelected = rowCount > 0 && numSelected === rowCount;
  const isIndeterminate = numSelected > 0 && numSelected < rowCount;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 3,
        }}>
        <CircularProgress sx={{ color: '#00B0FF' }} />
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        flexGrow: 1,
        width: '100%',
        boxSizing: 'border-box',
        borderTop: '2px solid #2e7d32',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollBehavior: 'smooth',
        userSelect: 'none',
      }}>
      {!selectedBatchId ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 3,
            textAlign: 'center',
          }}>
          <Typography color='text.secondary' variant='body2' sx={{ fontStyle: 'italic' }}>
            Select a batch from the pulldown menu above to view its markers.
          </Typography>
        </Box>
      ) : rowCount === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 3,
            textAlign: 'center',
          }}>
          <Typography color='text.secondary' variant='body2' sx={{ fontStyle: 'italic' }}>
            No markers found for this selection.
          </Typography>
        </Box>
      ) : (
        <Table stickyHeader size='small' sx={{ width: '100%' }}>
          <TableHead>
            <TableRow
              sx={{
                height: '28px',
                '& th': {
                  backgroundColor: '#e8f5e9',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #2e7d32',
                  py: '2px',
                  ...cellStyle,
                },
              }}>
              <TableCell padding='checkbox' sx={{ width: '50px', textAlign: 'center' }}>
                <Checkbox
                  color='primary'
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  size='small'
                  sx={{
                    p: 0.5,
                    '&.Mui-checked': { color: '#2e7d32' },
                    '&.MuiCheckbox-indeterminate': { color: '#2e7d32' },
                  }}
                />
              </TableCell>
              <TableCell align='left' sx={{ width: '50%' }}>
                <TableSortLabel
                  active={orderBy === 'title'}
                  direction={orderBy === 'title' ? order : 'asc'}
                  onClick={() => handleRequestSort('title')}
                  IconComponent={(props) => (
                    <CustomSortIcon {...props} active={orderBy === 'title'} />
                  )}
                  sx={sortLabelSx}>
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell align='left' sx={{ width: '25%' }}>
                <TableSortLabel
                  active={orderBy === 'category'}
                  direction={orderBy === 'category' ? order : 'asc'}
                  onClick={() => handleRequestSort('category')}
                  IconComponent={(props) => (
                    <CustomSortIcon {...props} active={orderBy === 'category'} />
                  )}
                  sx={sortLabelSx}>
                  Category
                </TableSortLabel>
              </TableCell>
              <TableCell align='left' sx={{ width: '25%' }}>
                <TableSortLabel
                  active={orderBy === 'region'}
                  direction={orderBy === 'region' ? order : 'asc'}
                  onClick={() => handleRequestSort('region')}
                  IconComponent={(props) => (
                    <CustomSortIcon {...props} active={orderBy === 'region'} />
                  )}
                  sx={sortLabelSx}>
                  Region
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enrichedAndSortedMarkers.map((row, index) => {
              const isSelected = selectedIds.includes(row.id);

              return (
                <TableRow
                  key={row.id}
                  hover={true}
                  selected={isSelected}
                  sx={{
                    height: '28px',
                    cursor: 'default',
                    '& td': { py: '2px', ...cellStyle },
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&.Mui-selected': { backgroundColor: 'transparent !important' },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                    },
                  }}>
                  <TableCell padding='checkbox' sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={isSelected}
                      onClick={(e) => handleCheckboxClick(e, row.id, index)}
                      size='small'
                      sx={{
                        p: 0.5,
                        '&.Mui-checked': { color: '#2e7d32' },
                      }}
                    />
                  </TableCell>
                  <TableCell align='left'>
                    <Typography variant='body2' sx={{ color: 'text.primary' }}>
                      {row.displayTitle}
                    </Typography>
                  </TableCell>
                  <TableCell align='left'>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {row.categoryName}
                    </Typography>
                  </TableCell>
                  <TableCell align='left'>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {row.regionName}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
}
